import $ from "jquery";
import auth, { post, get } from "./auth";

/**
 * Upselling Service
 * Manages AI-powered product recommendations based on current cart items
 * Tracks success metrics for continuous improvement
 */

const upsellService = {
  /**
   * Get personalized upselling suggestions based on cart items
   * @param {Array} cartItems - Current items in cart [{id, name, price, quantity, ...}]
   * @param {number} restaurantId - Restaurant identifier
   * @param {string} tableOccupationId - Current table/session identifier
   * @param {string} language - Language code for suggestions
   * @returns {Promise} jQuery deferred with suggestions
   */
  getUpsellingSuggestions: function(cartItems, restaurantId, tableOccupationId, language) {
    const def = $.Deferred();

    // Upselling works regardless of AI.enabled setting
    // This allows testing without full AI configuration

    // Prepare CartContext for AI service
    const cartContext = {
      cartItems: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        categoryId: item.categoryId,
        tableNumber: item.tableNumber
      })),
      totalPrice: cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      itemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      language: language || localStorage.language || 'en',
      tableOccupationId: tableOccupationId
    };

    // Call backend AI service for suggestions
    console.log("[UPSELL] Fetching suggestions for restaurant:", restaurantId);
    console.log("[UPSELL] Cart context:", cartContext);
    
    post(
      "aiService/" + restaurantId + "/upsellingSuggestions?lang=" + (language || 'en'),
      cartContext,
      undefined,
      false,
      0,
      false
    ).done(function(suggestions) {
      console.log("[UPSELL] Received suggestions:", suggestions);
      if (Array.isArray(suggestions)) {
        def.resolve(suggestions);
      } else if (suggestions && suggestions.suggestions) {
        def.resolve(suggestions.suggestions);
      } else {
        def.resolve([]);
      }
    }).fail(function(error) {
      auth.ajaxError = false; // Prevent global error handling for this specific call
      console.error("[UPSELL] Failed to get upselling suggestions:", error);
      console.warn("[UPSELL] Backend endpoint 'aiService/*/upsellingSuggestions' not yet implemented. Proceeding without suggestions.");
      def.resolve([]); // Gracefully handle failure
    });

    return def.promise();
  },

  /**
   * Record upselling metric - whether suggestion was accepted
   * @param {number} restaurantId - Restaurant identifier
   * @param {number} suggestionId - Suggestion that was offered
   * @param {number} menuItemId - Product that was suggested
   * @param {boolean} accepted - Whether user accepted the suggestion
   * @param {number} tableOccupationId - Context of the suggestion
   */
  recordMetric: function(restaurantId, suggestionId, menuItemId, accepted, tableOccupationId) {
    const def = $.Deferred();

    const metric = {
      suggestionId: suggestionId,
      menuItemId: menuItemId,
      accepted: accepted,
      tableOccupationId: tableOccupationId,
      timestamp: new Date().toISOString()
    };

    post(
      "aiService/" + restaurantId + "/upsellMetrics",
      metric,
      undefined,
      false,
      0,
      false
    ).done(function(response) {
      def.resolve(response);
    }).fail(function(error) {
      console.warn("Failed to record upselling metric:", error);
      def.resolve(); // Don't block on metrics recording
    });

    return def.promise();
  },

  /**
   * Add suggested item to cart
   * Convenience method to handle adding upsell items
   * @param {Object} menuItem - Menu item to add
   * @param {Function} onAddSuccess - Callback after item added
   * @returns {boolean} Success indicator
   */
  addToCart: function(menuItem, onAddSuccess) {
    // This delegates to the main orderMenuItem flow
    // which will be called from the component
    if (onAddSuccess) {
      onAddSuccess(menuItem);
    }
    return true;
  }
};

export default upsellService;
